// The looking-for-a-match state as a forged plate: the same bevel stack
// as the menu buttons, a brass-bezelled lamp that burns while listed, the
// title in the game face, elapsed time beneath, an eye that opens while
// searching. Click opens the panel. Lives in the Hall header and first in
// the Play menu, so a player always sees whether they are queued. While a
// four-seat pod gathers, a row of seat dots shows how many are present.
import { Component } from 'preact';
import { CONTENT_BG_DEFAULT } from '../lib/medievalTheme';
import { t } from '../localization/t';
import { IconEyeOpen, IconEyeClosed } from './ui/icons';

const LAMP_ON = '#6ac084';

function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// One socket per seat, lit for each player already present.
function SeatDots({ seats, present }) {
  return (
    <span className="inline-flex items-center" style={{ gap: '0.25cqw' }} role="img" aria-label={t('{present} of {seats} seats filled', { present, seats })}>
      {Array.from({ length: seats }, (_, i) => {
        const lit = i < present;
        return (
          <span
            key={i}
            data-seat={lit ? 'filled' : 'empty'}
            className="rounded-full"
            style={{
              width: '0.45cqw',
              height: '0.45cqw',
              background: lit
                ? 'radial-gradient(circle at 35% 35%, #d8ffe6 0%, #6ac084 45%, #2c7a4c 100%)'
                : 'radial-gradient(circle at 35% 35%, #3a3832 0%, #1c1b18 60%, #0e0e10 100%)',
              boxShadow: lit ? '0 0 5px rgba(106,192,132,0.7)' : 'inset 0 1px 1px rgba(0,0,0,0.9), 0 0 0 1px rgba(212,168,67,0.25)',
              transition: 'background 220ms ease, box-shadow 220ms ease',
            }}
          />
        );
      })}
    </span>
  );
}

const PLATE_IDLE = [
  'inset 0 1px 0 rgba(255, 230, 170, 0.10)',
  'inset 0 -1px 0 rgba(0, 0, 0, 0.30)',
  'inset 0 0 18px rgba(0, 0, 0, 0.45)',
  '0 0 0 1px rgba(212, 168, 67, 0.28)',
  '0 0 0 3px rgba(0, 0, 0, 0.55)',
  '0 0 0 4px rgba(180, 140, 60, 0.12)',
  '0 5px 14px rgba(0, 0, 0, 0.55)',
].join(', ');
const PLATE_HOVER = [
  'inset 0 1px 0 rgba(255, 235, 180, 0.16)',
  'inset 0 -1px 0 rgba(0, 0, 0, 0.30)',
  'inset 0 0 22px rgba(80, 45, 10, 0.35)',
  '0 0 0 1px rgba(232, 195, 110, 0.55)',
  '0 0 0 3px rgba(0, 0, 0, 0.55)',
  '0 0 0 4px rgba(212, 168, 67, 0.30)',
  '0 7px 18px rgba(0, 0, 0, 0.6), 0 0 20px rgba(212, 168, 67, 0.18)',
].join(', ');
const PLATE_LIT = [
  'inset 0 1px 0 rgba(255, 235, 180, 0.16)',
  'inset 0 -1px 0 rgba(0, 0, 0, 0.30)',
  'inset 0 0 22px rgba(80, 45, 10, 0.35)',
  '0 0 0 1px rgba(232, 195, 110, 0.55)',
  '0 0 0 3px rgba(0, 0, 0, 0.55)',
  '0 0 0 4px rgba(212, 168, 67, 0.22)',
  '0 5px 14px rgba(0, 0, 0, 0.55), 0 0 22px rgba(106, 192, 132, 0.16)',
].join(', ');

export default class LfgWidget extends Component {
  state = { now: Date.now(), hover: false };

  componentDidMount() { this._tick = setInterval(() => this.setState({ now: Date.now() }), 1000); }
  componentWillUnmount() { clearInterval(this._tick); }

  render() {
    const { lfg, onClick, width, height, radius, lampSize = '1.5cqw', fontSize = '1cqw', idleLabel = t('Not looking'), dataTutorial } = this.props;
    const { hover } = this.state;
    const active = !!lfg.active;
    const elapsed = active && lfg.since ? this.state.now - lfg.since : 0;
    const table = active && lfg.table?.seats > 2 ? lfg.table : null;
    return (
      <button
        type="button"
        onClick={onClick}
        data-tutorial={dataTutorial}
        aria-label={active ? t('Looking for a match. Edit or stop.') : t('Look for a match')}
        className="relative flex items-center text-left cursor-pointer shrink-0 active:scale-[0.98] overflow-hidden"
        style={{
          // The bevel stack draws 4px of rings outside the box. Pull the box
          // in by that much so the visible edge sits at the panel inset and
          // the visible height matches the tab trough beside it.
          width: `calc(${width} - 8px)`,
          height: `calc(${height} - 8px)`,
          margin: '4px',
          padding: '0 0.5cqw 0 0.625cqw',
          gap: '0.625cqw',
          borderRadius: radius,
          border: 'none',
          background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, ${active ? 'rgba(46,34,14,0.97)' : 'rgba(30,24,12,0.97)'} 0%, rgba(14,11,6,0.98) 100%)`,
          backgroundBlendMode: 'overlay, normal',
          boxShadow: active ? PLATE_LIT : hover ? PLATE_HOVER : PLATE_IDLE,
          transition: 'transform 160ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 220ms ease',
        }}
        onMouseEnter={() => this.setState({ hover: true })}
        onMouseLeave={() => this.setState({ hover: false })}
      >
        {/* Brass bezel with a glass lens. The socket is recessed so the
            lamp reads as a fitting on the plate, not a dot painted on it. */}
        <span
          className="grid place-items-center shrink-0 rounded-full"
          style={{
            width: lampSize,
            height: lampSize,
            background: 'linear-gradient(160deg, #e6c67a 0%, #9c7a34 45%, #5c4418 100%)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,240,200,0.35)',
          }}
        >
          <span
            className="grid place-items-center rounded-full"
            style={{ width: `calc(${lampSize} * 0.73)`, height: `calc(${lampSize} * 0.73)`, background: '#0a0805', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.95)' }}
          >
            <span
              className={`rounded-full ${active ? 'vh-lamp-blink' : ''}`}
              style={{
                width: `calc(${lampSize} * 0.4)`,
                height: `calc(${lampSize} * 0.4)`,
                background: active
                  ? 'radial-gradient(circle at 35% 35%, #d8ffe6 0%, #6ac084 45%, #2c7a4c 100%)'
                  : 'radial-gradient(circle at 35% 35%, #5a5750 0%, #2a2925 60%, #16161a 100%)',
                boxShadow: active ? '0 0 8px #6ac084, 0 0 16px rgba(106,192,132,0.6)' : 'inset 0 1px 1px rgba(255,255,255,0.08)',
                transition: 'background 220ms ease, box-shadow 220ms ease',
              }}
            />
          </span>
        </span>

        {/* Title and meta stack rather than sitting side by side: the elapsed
            time plus the widened-search note is too much to carry on one
            baseline without crowding the plate. */}
        <span className="flex flex-col justify-center min-w-0 flex-1 whitespace-nowrap" style={{ lineHeight: 1.1, gap: '0.15cqw' }}>
            <span
              className="arena-heading"
              style={{
                fontSize,
                color: active ? '#f4e4b8' : '#d7d2ca',
                textShadow: '0 -1px 0 rgba(0,0,0,0.8), 0 1px 0 rgba(255,220,140,0.18), 0 2px 5px rgba(0,0,0,0.8)',
              }}
            >
              {active ? t('Looking for a match') : idleLabel}
            </span>
            {active ? (
              <span className="inline-flex items-center text-[0.6cqw] tabular-nums font-semibold" style={{ color: LAMP_ON, textShadow: '0 0 8px rgba(106,192,132,0.5)', gap: '0.5cqw' }}>
                <span>{elapsed >= 5 * 60 * 1000 ? t('{time} · any rank', { time: formatElapsed(elapsed) }) : formatElapsed(elapsed)}</span>
                {table ? <SeatDots seats={table.seats} present={table.present} /> : null}
              </span>
            ) : null}
        </span>

        {/* Open eye while searching, closed eye at rest. Both stay mounted
            and cross-fade with scale + blur so the swap reads as one eye
            opening, not two glyphs trading places. */}
        <span className="relative shrink-0" style={{ width: '1.4cqw', height: '1.4cqw' }}>
          {[['open', active], ['closed', !active]].map(([kind, shown]) => (
            <span
              key={kind}
              className="absolute inset-0 grid place-items-center"
              style={{
                opacity: shown ? 1 : 0,
                transform: shown ? 'scale(1)' : 'scale(0.25)',
                filter: shown ? 'blur(0px)' : 'blur(4px)',
                transition: 'opacity 300ms cubic-bezier(0.2, 0, 0, 1), transform 300ms cubic-bezier(0.2, 0, 0, 1), filter 300ms cubic-bezier(0.2, 0, 0, 1)',
              }}
            >
              {kind === 'open'
                ? <IconEyeOpen size={22} color="#f0d060" style={{ width: '1.4cqw', height: '1.4cqw', filter: 'drop-shadow(0 0 6px rgba(240,208,96,0.45))' }} />
                : <IconEyeClosed size={22} color={hover ? '#c9b98a' : 'rgba(166,160,155,0.75)'} style={{ width: '1.4cqw', height: '1.4cqw', transition: 'color 200ms ease' }} />}
            </span>
          ))}
        </span>
      </button>
    );
  }
}
